/**
 * Netzwerk-Service — Practice-Web (Praxis-Perspektive)
 *
 * Suche nach Praxen/Einrichtungen, Partnerschaften anfragen und verwalten.
 * RPCs: search_network_practices, search_network_facilities,
 *       request_organization_partnership, respond_to_partnership,
 *       get_organization_partners, get_pending_partnership_requests
 */

// TI-BOUNDARY: Netzwerk-Management ist reine Orchestrierung.
// Keine TI/KIM/ePA-Operationen. Siehe: docs/architecture/REFERRAL_COMPLIANCE_BOUNDARY.md
import { requireSupabase, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    OrganizationPartnership,
    NetworkPracticeSearchResult,
    NetworkFacilitySearchResult,
    OrganizationType,
    NetworkDashboardStats,
    PartnerInteropStats,
    NetworkActivityItem,
} from "@/types/organization-partnership";
import {
    mapPartnershipRow,
    mapPartnerRpcRow,
    mapPendingRequestRpcRow,
    mapPracticeSearchRow,
    mapFacilitySearchRow,
    mapDashboardStatsRow,
    mapPartnerInteropRow,
    mapActivityFeedRow,
} from "@/services/organization-partnership.shared";

// ── Helpers ─────────────────────────────────────────────────

function getSingleRpcRecord(data: unknown): Record<string, unknown> | null {
    if (!data) return null;
    if (Array.isArray(data)) {
        const [first] = data;
        return (first as Record<string, unknown> | undefined) ?? null;
    }
    return data as Record<string, unknown>;
}

function getRpcArray(data: unknown): Record<string, unknown>[] {
    if (!data) return [];
    if (Array.isArray(data)) return data as Record<string, unknown>[];
    return [];
}

// ── Search ──────────────────────────────────────────────────

export async function searchNetworkPractices(
    query: string,
    postalCode?: string,
    specialty?: string,
): Promise<NetworkPracticeSearchResult[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("search_network_practices", {
            p_query: query,
            p_postal_code: postalCode ?? null,
            p_specialty: specialty ?? null,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPracticeSearchRow);
    } catch (e) {
        logger.error("network.searchPractices", e);
        return [];
    }
}

export async function searchNetworkFacilities(
    query: string,
    postalCode?: string,
    facilityType?: string,
): Promise<NetworkFacilitySearchResult[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("search_network_facilities", {
            p_query: query,
            p_postal_code: postalCode ?? null,
            p_facility_type: facilityType ?? null,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapFacilitySearchRow);
    } catch (e) {
        logger.error("network.searchFacilities", e);
        return [];
    }
}

// ── Partnerships ────────────────────────────────────────────

export async function requestPartnership(
    requesterType: "practice",
    requesterId: string,
    targetType: OrganizationType,
    targetId: string,
    categories: string[],
    message?: string,
): Promise<OrganizationPartnership | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();
        const { data, error } = await sb.rpc("request_organization_partnership", {
            p_requester_type: requesterType,
            p_requester_id: requesterId,
            p_target_type: targetType,
            p_target_id: targetId,
            p_categories: categories,
            p_message: message ?? null,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapPartnershipRow(row) : null;
    } catch (e) {
        logger.error("network.requestPartnership", e);
        return null;
    }
}

export async function respondToPartnership(
    partnershipId: string,
    accept: boolean,
    rejectionReason?: string,
): Promise<OrganizationPartnership | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();
        const { data, error } = await sb.rpc("respond_to_partnership", {
            p_partnership_id: partnershipId,
            p_accept: accept,
            p_rejection_reason: rejectionReason ?? null,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapPartnershipRow(row) : null;
    } catch (e) {
        logger.error("network.respondToPartnership", e);
        return null;
    }
}

export async function getPartners(
    practiceId: string,
): Promise<OrganizationPartnership[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_organization_partners", {
            p_org_type: "practice",
            p_org_id: practiceId,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPartnerRpcRow);
    } catch (e) {
        logger.error("network.getPartners", e);
        return [];
    }
}

export async function getPendingRequests(
    practiceId: string,
): Promise<OrganizationPartnership[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_pending_partnership_requests", {
            p_org_type: "practice",
            p_org_id: practiceId,
        });
        if (error) throw error;
        return getRpcArray(data).map(mapPendingRequestRpcRow);
    } catch (e) {
        logger.error("network.getPendingRequests", e);
        return [];
    }
}

// ── Dashboard & Activity ────────────────────────────────────

export async function getDashboardStats(
    orgType: OrganizationType,
    orgId: string,
): Promise<NetworkDashboardStats> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_dashboard_stats", {
            p_org_type: orgType,
            p_org_id: orgId,
        });
        if (error) throw error;
        return mapDashboardStatsRow(getSingleRpcRecord(data) ?? {});
    } catch (e) {
        logger.error("network.getDashboardStats", e);
        return { activePartners: 0, pendingRequests: 0, activeCareLinks: 0, openCareRequests: 0, activeReferrals: 0 };
    }
}

export async function getPartnerInteropStats(
    orgType: OrganizationType,
    orgId: string,
): Promise<PartnerInteropStats[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_partner_interop_stats", {
            p_org_type: orgType,
            p_org_id: orgId,
        });
        if (error) throw error;
        return getRpcArray(data).map((row) => mapPartnerInteropRow(row));
    } catch (e) {
        logger.error("network.getPartnerInteropStats", e);
        return [];
    }
}

/** Returns open request counts per facility grouped by category. */
export async function getOpenRequestBreakdown(
    practiceId: string,
): Promise<Map<string, Record<string, number>>> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb
            .from("care_practice_requests")
            .select("facility_id, category")
            .eq("practice_id", practiceId)
            .not("status", "eq", "ERLEDIGT");
        if (error) throw error;
        const result = new Map<string, Record<string, number>>();
        for (const row of (data ?? []) as Array<{ facility_id: string; category: string }>) {
            if (!row.facility_id) continue;
            const bucket = result.get(row.facility_id) ?? {};
            bucket[row.category] = (bucket[row.category] ?? 0) + 1;
            result.set(row.facility_id, bucket);
        }
        return result;
    } catch (e) {
        logger.error("network.getOpenRequestBreakdown", e);
        return new Map();
    }
}

export async function getActivityFeed(
    orgType: OrganizationType,
    orgId: string,
    limit = 20,
): Promise<NetworkActivityItem[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("get_network_activity_feed", {
            p_org_type: orgType,
            p_org_id: orgId,
            p_limit: limit,
        });
        if (error) throw error;
        return getRpcArray(data).map((row) => mapActivityFeedRow(row));
    } catch (e) {
        logger.error("network.getActivityFeed", e);
        return [];
    }
}

// ── Einladungslinks ─────────────────────────────────────────

export interface NetworkInvite {
    id: string;
    token: string;
    expiresAt: string;
    status: string;
}

export interface InvitePublicInfo {
    valid: boolean;
    practiceName?: string;
    specialty?: string;
    city?: string;
    expiresAt?: string;
    reason?: string;
}

export interface InviteAcceptResult {
    partnershipId: string;
    inviterPracticeId: string;
    inviterName: string;
    accepterPracticeId: string;
    accepterName: string;
    status: string;
}

/**
 * Generiert einen Einladungslink fuer Practice-to-Practice Vernetzung (30 Tage gueltig)
 */
export async function generateNetworkInvite(
    practiceId: string,
): Promise<NetworkInvite | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();

        const { data, error } = await sb.rpc("generate_network_invite", {
            p_practice_id: practiceId,
        });

        if (error) throw error;

        const row = getSingleRpcRecord(data);
        if (!row) return null;

        return {
            id: row.id as string,
            token: row.token as string,
            expiresAt: row.expires_at as string,
            status: row.status as string,
        };
    } catch (e) {
        logger.error("network.generateNetworkInvite", e);
        throw e;
    }
}

/**
 * Holt oeffentliche Infos einer Einladung (ohne Auth)
 */
export async function getInvitePublicInfo(
    token: string,
): Promise<InvitePublicInfo> {
    try {
        const sb = requireSupabase();

        const { data, error } = await sb.rpc("get_invite_public_info", {
            p_token: token,
        });

        if (error) throw error;

        const row = getSingleRpcRecord(data);
        if (!row) return { valid: false, reason: "Einladung nicht gefunden" };

        return {
            valid: row.valid as boolean,
            practiceName: row.practice_name as string | undefined,
            specialty: row.specialty as string | undefined,
            city: row.city as string | undefined,
            expiresAt: row.expires_at as string | undefined,
            reason: row.reason as string | undefined,
        };
    } catch (e) {
        logger.error("network.getInvitePublicInfo", e);
        return { valid: false, reason: "Fehler beim Laden der Einladung" };
    }
}

/**
 * Nimmt eine Netzwerk-Einladung an und erstellt eine bilaterale Partnerschaft
 */
export async function acceptNetworkInvite(
    token: string,
): Promise<InviteAcceptResult | null> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();

        const { data, error } = await sb.rpc("accept_network_invite", {
            p_token: token,
        });

        if (error) throw error;

        const row = getSingleRpcRecord(data);
        if (!row) return null;

        return {
            partnershipId: row.partnership_id as string,
            inviterPracticeId: row.inviter_practice_id as string,
            inviterName: row.inviter_name as string,
            accepterPracticeId: row.accepter_practice_id as string,
            accepterName: row.accepter_name as string,
            status: row.status as string,
        };
    } catch (e) {
        logger.error("network.acceptNetworkInvite", e);
        throw e;
    }
}

/**
 * Holt alle aktiven Einladungen einer Praxis
 */
export async function getActiveInvites(
    practiceId: string,
): Promise<NetworkInvite[]> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();

        const { data, error } = await sb
            .from("network_invites")
            .select("id, token, expires_at, status")
            .eq("practice_id", practiceId)
            .eq("status", "pending")
            .gt("expires_at", new Date().toISOString())
            .order("created_at", { ascending: false });

        if (error) throw error;

        return (data ?? []).map((row: Record<string, unknown>) => ({
            id: row.id as string,
            token: row.token as string,
            expiresAt: row.expires_at as string,
            status: row.status as string,
        }));
    } catch (e) {
        logger.error("network.getActiveInvites", e);
        return [];
    }
}

/**
 * Widerruft eine Einladung
 */
export async function revokeNetworkInvite(
    inviteId: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        await ensureValidSession();

        const { error } = await sb
            .from("network_invites")
            .update({ status: "revoked" })
            .eq("id", inviteId);

        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("network.revokeNetworkInvite", e);
        throw e;
    }
}
